import React, { Fragment, memo, useState, useCallback, useContext, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import tw from 'twin.macro'
import { GSIcon } from 'src/components/shared/GSIcon'
import { Button } from 'src/components/shared/Button'
import { SensitiveActionModal } from 'src/components/shared/SensitiveActionModal'
import { RejectOnboarding, RecollectOnboardingStep } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'
import { CollectedDataStepType } from 'src/constants/collectedData'
import { ContractorDetailsModalContext } from 'src/components/contractor-details/ContractorDetailsModal.context'

export interface ContractorStepDetailsRecollectButtonProps {
  collectDataStep: { type: CollectedDataStepType; id: string }
  partnerDriverId: string
  onAction: () => void | Promise<void>
  className?: string
}

const ContractorStepDetailsRecollectButtonComponent = ({
  collectDataStep,
  partnerDriverId,
  onAction,
  className,
}: ContractorStepDetailsRecollectButtonProps) => {
  const dispatch = useDispatch()

  const { triggerStatusDelayedContractorRefresh } = useContext(ContractorDetailsModalContext)

  const [isLoading, setIsLoading] = useState(false)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const isMountedRef = useRef(true)
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const openConfirm = useCallback(() => {
    setIsConfirmOpen(true)
  }, [])
  const closeConfirm = useCallback(() => {
    setIsConfirmOpen(false)
  }, [])

  const rejectContractor = useCallback(async () => {
    try {
      triggerStatusDelayedContractorRefresh()
      await createAsyncAction(dispatch, RejectOnboarding.request({ partnerDriverId }))

      await onAction()

      if (isMountedRef.current) {
        showToast('Contractor is rejected.')
      }
    } catch (err) {
      handleApiError(err)
    }
  }, [partnerDriverId, dispatch, onAction, triggerStatusDelayedContractorRefresh])

  const handleRecollectConfirm = useCallback(async () => {
    setIsLoading(true)
    try {
      if (collectDataStep.type === CollectedDataStepType.InPersonMeeting) {
        await rejectContractor()
      } else {
        triggerStatusDelayedContractorRefresh()
        await createAsyncAction(
          dispatch,
          RecollectOnboardingStep.request({
            stepId: collectDataStep.id,
            partnerDriverId,
          }),
        )

        await onAction()

        if (isMountedRef.current) {
          showToast('Step is sent to recollection.')
        }
      }
    } catch (err) {
      handleApiError(err)
    }

    if (isMountedRef.current) {
      setIsLoading(false)
      closeConfirm()
    }
  }, [
    collectDataStep,
    partnerDriverId,
    rejectContractor,
    dispatch,
    onAction,
    closeConfirm,
    triggerStatusDelayedContractorRefresh,
  ])

  const handleRecollect = useCallback(() => {
    openConfirm()
  }, [openConfirm])

  return (
    <Fragment>
      <Button
        onClick={handleRecollect}
        disabled={isLoading}
        loading={isLoading}
        size="small"
        color="grey"
        className={[tw`w-full mt-4`, className].filter(Boolean)}
        rightIcon={<GSIcon iconName="gs_arrows_repeat_right_left" width={20} />}
      >
        Recollect
      </Button>
      <SensitiveActionModal
        isOpen={isConfirmOpen}
        title="Recollect step?"
        text="Are you sure you want to initiate recollection?"
        submitLabel="Yes"
        onClose={closeConfirm}
        onSubmit={handleRecollectConfirm}
      />
    </Fragment>
  )
}

export const ContractorStepDetailsRecollectButton = memo(
  ContractorStepDetailsRecollectButtonComponent,
)
